import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { listFrom, loadErrorMessage } from '../../utils/loadState'
import { uploadDocumentAdvanced, getDocuments, deleteDocument } from '../../api'
import './KnowledgeBase.css'

const TRUTH_TYPES = [
  { id: 'truth_3_law', label: 'Compliance / Legal', icon: '⚖️', desc: 'FLSA, state laws, regulations, tax rules', color: '#c0334e' },
  { id: 'truth_4_product', label: 'Vendor Documentation', icon: '📘', desc: 'Product guides, release notes, how-tos', color: '#2d5bc0' },
  { id: 'truth_5_config', label: 'Config / Best Practices', icon: '🔧', desc: 'Implementation guides, config standards', color: '#7c3aed' },
]

const VENDORS = [
  { id: '', label: 'None / General' },
  { id: 'ukg', label: 'UKG' },
  { id: 'adp', label: 'ADP' },
  { id: 'workday', label: 'Workday' },
  { id: 'oracle', label: 'Oracle' },
  { id: 'sap', label: 'SAP' },
  { id: 'greenhouse', label: 'Greenhouse' },
  { id: 'salesforce', label: 'Salesforce' },
  { id: 'benefitfocus', label: 'Benefitfocus' },
  { id: 'cornerstone', label: 'Cornerstone' },
]

// Products by vendor. Product IDs match _PRODUCT_NORMALIZATION values in
// routers/chat.py so retrieval filters line up exactly. When onboarding a new
// vendor/product, add it here AND to _PRODUCT_NORMALIZATION.
const PRODUCTS_BY_VENDOR = {
  ukg: [
    { id: 'UKG_PRO', label: 'UKG Pro' },
    { id: 'UKG_WFM', label: 'UKG Workforce Management' },
  ],
  adp: [
    { id: 'ADP_WFN', label: 'ADP Workforce Now' },
    { id: 'ADP_RUN', label: 'ADP RUN' },
  ],
  workday: [
    { id: 'WORKDAY_HCM', label: 'Workday HCM' },
  ],
  oracle: [
    { id: 'ORACLE_HCM', label: 'Oracle HCM Cloud' },
    { id: 'NETSUITE', label: 'NetSuite' },
  ],
  sap: [
    { id: 'SAP_SUCCESSFACTORS', label: 'SAP SuccessFactors' },
  ],
  greenhouse: [
    { id: 'GREENHOUSE', label: 'Greenhouse' },
  ],
  salesforce: [
    { id: 'SALESFORCE', label: 'Salesforce' },
  ],
  benefitfocus: [
    { id: 'BENEFITFOCUS', label: 'Benefitfocus' },
  ],
  cornerstone: [
    { id: 'CORNERSTONE', label: 'Cornerstone' },
  ],
}

// Functional areas matching the vocabulary used by calc specs in
// engines/expected_calculations.py and the retrieval helper in chat.py.
// 'general' is the cross-cutting default for docs that apply broadly.
const FUNCTIONAL_AREAS = [
  { id: 'general', label: 'General' },
  { id: 'payroll', label: 'Payroll' },
  { id: 'core_hr', label: 'Core HR' },
  { id: 'wfm', label: 'Workforce Management' },
  { id: 'benefits', label: 'Benefits' },
  { id: 'tax', label: 'Tax' },
  { id: 'compliance', label: 'Compliance' },
]

const SUPPORTED_TYPES = ['.pdf', '.docx', '.doc', '.pptx', '.txt', '.html', '.md', '.csv', '.xlsx', '.eml']

export default function KnowledgeBase() {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [documents, setDocuments] = useState([])
  const [loadingDocs, setLoadingDocs] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  // Upload state
  const [uploadQueue, setUploadQueue] = useState([]) // files waiting + in progress
  const [uploadedFiles, setUploadedFiles] = useState([]) // successfully uploaded this session
  const [truthType, setTruthType] = useState('truth_4_product')
  const [vendorId, setVendorId] = useState('')
  const [productId, setProductId] = useState('')
  const [functionalArea, setFunctionalArea] = useState('general')
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)

  // Available products for the currently selected vendor
  const availableProducts = vendorId ? (PRODUCTS_BY_VENDOR[vendorId] || []) : []

  // When vendor changes, clear any stale product selection
  function handleVendorChange(newVendorId) {
    setVendorId(newVendorId)
    setProductId('') // always reset; user re-picks for the new vendor
  }

  // Load existing documents
  useEffect(() => {
    loadDocuments()
  }, [])

  // A failed load is an error with a Retry, never "No documents in the global knowledge
  // base yet" (item 201). A failed read of the document store answers an HTTP error.
  const [docsError, setDocsError] = useState(null)

  async function loadDocuments() {
    setLoadingDocs(true)
    setDocsError(null)
    try {
      const data = await getDocuments('_global')
      setDocuments(listFrom(data, 'documents'))
    } catch (err) {
      console.error('Failed to load documents:', err)
      setDocuments([])
      setDocsError(loadErrorMessage(err))
    } finally {
      setLoadingDocs(false)
    }
  }

  // ── Drag & Drop ──
  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    addFiles(files)
  }, [])

  function addFiles(fileList) {
    const newFiles = Array.from(fileList)
      .filter(f => {
        const ext = '.' + f.name.split('.').pop().toLowerCase()
        return SUPPORTED_TYPES.includes(ext)
      })
      .map(f => ({
        id: `${f.name}-${Date.now()}-${Math.random()}`,
        file: f,
        name: f.name,
        size: f.size,
        status: 'queued', // queued | uploading | success | error
        chunks: 0,
        error: null,
      }))

    if (newFiles.length === 0) return
    setUploadQueue(prev => [...prev, ...newFiles])
  }

  function removeFromQueue(id) {
    setUploadQueue(prev => prev.filter(f => f.id !== id))
  }

  function clearCompleted() {
    setUploadQueue(prev => prev.filter(f => f.status !== 'success'))
  }

  // ── Upload all ──
  async function startUpload() {
    const queued = uploadQueue.filter(f => f.status === 'queued')
    if (queued.length === 0) return

    setUploading(true)
    const CONCURRENCY = 3

    // Process in batches
    for (let i = 0; i < queued.length; i += CONCURRENCY) {
      const batch = queued.slice(i, i + CONCURRENCY)

      // Mark batch as uploading
      setUploadQueue(prev => prev.map(f =>
        batch.find(b => b.id === f.id) ? { ...f, status: 'uploading' } : f
      ))

      const results = await Promise.allSettled(
        batch.map(async (item) => {
          try {
            const result = await uploadDocumentAdvanced(
              item.file,
              '_global',
              truthType,
              vendorId || null,
              productId || null,
              functionalArea || null,
            )
            return { id: item.id, success: result?.success !== false, chunks: result?.chunks_created || 0, error: result?.errors?.[0] }
          } catch (err) {
            return { id: item.id, success: false, chunks: 0, error: err.message }
          }
        })
      )

      // Update statuses
      setUploadQueue(prev => prev.map(f => {
        const result = results.find(r => r.status === 'fulfilled' && r.value.id === f.id)?.value
          || results.find(r => r.status === 'rejected' && f.id === batch.find(b => true)?.id)?.reason
        if (!result) return f
        return {
          ...f,
          status: result.success ? 'success' : 'error',
          chunks: result.chunks || 0,
          error: result.error || null,
        }
      }))

      // Track successful uploads locally
      const successes = results
        .filter(r => r.status === 'fulfilled' && r.value.success)
        .map(r => {
          const item = batch.find(b => b.id === r.value.id)
          return {
            file_name: item?.name || 'unknown',
            truth_type: truthType,
            vendor_id: vendorId || null,
            product_id: productId || null,
            functional_area: functionalArea || null,
            chunks: r.value.chunks || 0,
            ingested_at: new Date().toISOString(),
            document_type: (item?.name || '').split('.').pop()?.toUpperCase() || '?',
            _local: true,
          }
        })
      if (successes.length > 0) {
        setUploadedFiles(prev => [...prev, ...successes])
      }
    }

    setUploading(false)
    loadDocuments() // Refresh the document list
  }

  async function handleDelete(fileName) {
    if (!confirm(`Delete "${fileName}" from global knowledge base?`)) return
    try {
      await deleteDocument('_global', fileName)
      loadDocuments()
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  // Stats
  const queuedCount = uploadQueue.filter(f => f.status === 'queued').length
  const uploadingCount = uploadQueue.filter(f => f.status === 'uploading').length
  const successCount = uploadQueue.filter(f => f.status === 'success').length
  const errorCount = uploadQueue.filter(f => f.status === 'error').length
  const totalChunks = uploadQueue.reduce((sum, f) => sum + (f.chunks || 0), 0)

  // Merge backend docs + locally tracked uploads (deduplicate by file_name)
  const allDocs = [...documents]
  for (const local of uploadedFiles) {
    if (!allDocs.find(d => (d.file_name || d.name) === local.file_name)) {
      allDocs.push(local)
    }
  }

  const filteredDocs = allDocs.filter(d =>
    (d.file_name || d.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (d.truth_type || '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  const truthConfig = TRUTH_TYPES.find(t => t.id === truthType) || TRUTH_TYPES[1]
  const productLabel = productId ? (availableProducts.find(p => p.id === productId)?.label || productId) : null
  const functionalAreaLabel = FUNCTIONAL_AREAS.find(fa => fa.id === functionalArea)?.label || functionalArea

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Knowledge Base' },
  ]

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} />

      <div className="kb-page">
        <div className="kb-container">

          {/* Header */}
          <div className="kb-header">
            <div>
              <h1 className="kb-title">📚 Global Knowledge Base</h1>
              <p className="kb-subtitle">Upload compliance docs, vendor guides, and best practices. These are available across all customers.</p>
            </div>
          </div>

          {/* Upload Section */}
          <div className="kb-upload-section">
            <div className="kb-upload-header">
              <h2 className="kb-section-title">Upload Documents</h2>
            </div>

            {/* Tagging controls */}
            <div className="kb-tag-controls">
              <div className="kb-tag-group">
                <label className="kb-tag-label">Truth Type</label>
                <div className="kb-truth-picker">
                  {TRUTH_TYPES.map(t => (
                    <button
                      key={t.id}
                      className={`kb-truth-btn ${truthType === t.id ? 'active' : ''}`}
                      onClick={() => setTruthType(t.id)}
                      style={truthType === t.id ? { borderColor: t.color, background: t.color + '0a' } : {}}
                    >
                      <span className="kb-truth-icon">{t.icon}</span>
                      <div>
                        <div className="kb-truth-label">{t.label}</div>
                        <div className="kb-truth-desc">{t.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="kb-tag-group">
                <label className="kb-tag-label">Vendor (optional)</label>
                <select className="kb-select" value={vendorId} onChange={e => handleVendorChange(e.target.value)}>
                  {VENDORS.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                </select>
              </div>
              {vendorId && availableProducts.length > 0 && (
                <div className="kb-tag-group">
                  <label className="kb-tag-label">Product (optional)</label>
                  <select className="kb-select" value={productId} onChange={e => setProductId(e.target.value)}>
                    <option value="">— None / All products —</option>
                    {availableProducts.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
              )}
              <div className="kb-tag-group">
                <label className="kb-tag-label">Functional Area</label>
                <select className="kb-select" value={functionalArea} onChange={e => setFunctionalArea(e.target.value)}>
                  {FUNCTIONAL_AREAS.map(fa => <option key={fa.id} value={fa.id}>{fa.label}</option>)}
                </select>
              </div>
            </div>

            {/* Drop zone */}
            <div
              className={`kb-dropzone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={SUPPORTED_TYPES.join(',')}
                style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)}
              />
              <div className="kb-dropzone-icon">{isDragging ? '📥' : '📄'}</div>
              <div className="kb-dropzone-text">
                {isDragging ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
              </div>
              <div className="kb-dropzone-hint">
                PDF, Word, PowerPoint, Excel, text files · No size limit · Multiple files OK
              </div>
            </div>

            {/* Upload Queue */}
            {uploadQueue.length > 0 && (
              <div className="kb-queue">
                <div className="kb-queue-header">
                  <div className="kb-queue-stats">
                    <span className="kb-queue-stat">
                      <strong>{uploadQueue.length}</strong> files
                    </span>
                    {successCount > 0 && (
                      <span className="kb-queue-stat success">✓ {successCount} uploaded ({totalChunks} chunks)</span>
                    )}
                    {errorCount > 0 && (
                      <span className="kb-queue-stat error">✗ {errorCount} failed</span>
                    )}
                    {uploadingCount > 0 && (
                      <span className="kb-queue-stat uploading">⟳ {uploadingCount} uploading…</span>
                    )}
                  </div>
                  <div className="kb-queue-actions">
                    {successCount > 0 && (
                      <button className="kb-btn-text" onClick={clearCompleted}>Clear completed</button>
                    )}
                    <button
                      className="kb-btn-primary"
                      onClick={startUpload}
                      disabled={uploading || queuedCount === 0}
                    >
                      {uploading ? `Uploading… (${uploadingCount} active)` : `Upload ${queuedCount} file${queuedCount !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>

                {/* Tag banner */}
                <div className="kb-queue-tag-banner" style={{ borderLeftColor: truthConfig.color }}>
                  <span>{truthConfig.icon}</span>
                  <span>All files will be tagged as <strong>{truthConfig.label}</strong></span>
                  {vendorId && <span> · Vendor: <strong>{VENDORS.find(v => v.id === vendorId)?.label}</strong></span>}
                  {productLabel && <span> · Product: <strong>{productLabel}</strong></span>}
                  <span> · Area: <strong>{functionalAreaLabel}</strong></span>
                </div>

                <div className="kb-queue-list">
                  {uploadQueue.map(item => (
                    <div key={item.id} className={`kb-queue-item ${item.status}`}>
                      <div className="kb-queue-item-icon">
                        {item.status === 'queued' && '📄'}
                        {item.status === 'uploading' && <span className="kb-spinner-sm" />}
                        {item.status === 'success' && '✅'}
                        {item.status === 'error' && '❌'}
                      </div>
                      <div className="kb-queue-item-info">
                        <div className="kb-queue-item-name">{item.name}</div>
                        <div className="kb-queue-item-meta">
                          {formatSize(item.size)}
                          {item.status === 'success' && ` · ${item.chunks} chunks indexed`}
                          {item.status === 'error' && <span className="kb-queue-error"> · {item.error}</span>}
                          {item.status === 'uploading' && ' · Processing…'}
                        </div>
                      </div>
                      {item.status === 'queued' && (
                        <button className="kb-queue-remove" onClick={() => removeFromQueue(item.id)}>×</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Existing Documents */}
          <div className="kb-docs-section">
            <div className="kb-docs-header">
              <h2 className="kb-section-title">Indexed Documents</h2>
              <input
                className="kb-search"
                placeholder="Search documents…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            {loadingDocs ? (
              <div className="kb-loading">Loading documents…</div>
            ) : docsError ? (
              <LoadError what="documents" error={docsError} onRetry={loadDocuments} />
            ) : filteredDocs.length === 0 ? (
              <div className="kb-empty">
                {documents.length === 0
                  ? 'No documents in the global knowledge base yet. Upload some above!'
                  : 'No documents match your search.'}
              </div>
            ) : (
              <div className="kb-docs-list">
                <div className="kb-docs-list-header">
                  <span>Document</span>
                  <span>Type</span>
                  <span>Chunks</span>
                  <span>Uploaded</span>
                  <span></span>
                </div>
                {filteredDocs.map((doc, i) => {
                  const tt = TRUTH_TYPES.find(t => t.id === doc.truth_type)
                  return (
                    <div key={i} className="kb-doc-row">
                      <div className="kb-doc-name">
                        <span className="kb-doc-icon">📄</span>
                        {doc.file_name || doc.name || 'Unknown'}
                      </div>
                      <div className="kb-doc-type">
                        {tt ? (
                          <span className="kb-doc-type-badge" style={{ color: tt.color, background: tt.color + '12' }}>
                            {tt.icon} {tt.label}
                          </span>
                        ) : (
                          <span className="kb-doc-type-badge">{doc.truth_type || '—'}</span>
                        )}
                      </div>
                      <div className="kb-doc-chunks">{doc.chunk_count || doc.chunks || '—'}</div>
                      <div className="kb-doc-date">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}</div>
                      <button className="kb-doc-delete" onClick={() => handleDelete(doc.file_name || doc.name)}>×</button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  )
}

function formatSize(bytes) {
  if (!bytes) return '0B'
  if (bytes < 1024) return bytes + 'B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB'
}
